import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { rupee } from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import CustomerLayout from "@/components/CustomerLayout";
import StoreCard from "@/components/StoreCard";
import ProductOffers from "@/components/ProductOffers";
import { EmptyState, Spinner } from "@/components/States";
import { Search as SearchIcon, Clock, Store, MapPin, Package } from "lucide-react";

const RECENT_KEY = "gf_recent_searches";
const POPULAR = ["Milk", "Atta", "Tomato", "Coke", "Bread", "Rice"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const { location } = useLocation();
  const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; } });

  useEffect(() => {
    if (!q) { setData(null); return; }
    setLoading(true);
    api.get("/catalog/search", { params: { q, lat: location.lat, lng: location.lng } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
    setRecent((prev) => { const next = [q, ...prev.filter((x) => x !== q)].slice(0, 6); localStorage.setItem(RECENT_KEY, JSON.stringify(next)); return next; });
  }, [q]); // eslint-disable-line

  const submit = (e) => { e.preventDefault(); if (input.trim()) setParams({ q: input.trim() }); };

  return (
    <CustomerLayout>
      <form onSubmit={submit} className="relative mb-5">
        <SearchIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <input autoFocus value={input} onChange={(e) => setInput(e.target.value)} placeholder="Search groceries, products or stores..."
          className="w-full rounded-2xl border border-slate-200 bg-white py-3.5 pl-12 pr-4 text-base outline-none focus:border-brand" data-testid="search-page-input" />
      </form>

      {!q && (
        <div className="mb-6 space-y-4">
          {recent.length > 0 && (
            <div><p className="mb-2 text-sm font-semibold text-slate-500">Recent searches</p>
              <div className="flex flex-wrap gap-2">{recent.map((r) => <button key={r} onClick={() => setParams({ q: r })} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600"><Clock className="h-3.5 w-3.5" />{r}</button>)}</div>
            </div>
          )}
          <div><p className="mb-2 text-sm font-semibold text-slate-500">Popular searches</p>
            <div className="flex flex-wrap gap-2">{POPULAR.map((r) => <button key={r} onClick={() => setParams({ q: r })} className="rounded-full bg-brand-soft px-3 py-1.5 text-sm font-semibold text-brand">{r}</button>)}</div>
          </div>
        </div>
      )}

      {loading && <div className="flex justify-center py-12"><Spinner className="h-8 w-8" /></div>}

      {data && !loading && (
        <>
          {data.stores?.length > 0 && (
            <section className="mb-8">
              <h2 className="mb-3 font-head text-lg font-bold text-slate-900">Stores ({data.stores.length})</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">{data.stores.map((s) => <StoreCard key={s.id} store={s} />)}</div>
            </section>
          )}
          {data.groups?.length > 0 ? (
            <section>
              <h2 className="mb-3 font-head text-lg font-bold text-slate-900">Products ({data.groups.length})</h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5" data-testid="search-groups">
                {data.groups.map((g, i) => (
                  <button key={g.master_id || i} onClick={() => setActive(g)} data-testid="search-group-card"
                    className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-left transition-all hover:-translate-y-0.5 hover:shadow-md">
                    <div className="relative aspect-square w-full overflow-hidden bg-slate-100">
                      {g.image ? <img src={g.image} alt={g.name} loading="lazy" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Package className="h-8 w-8 text-slate-300" /></div>}
                    </div>
                    <div className="flex flex-1 flex-col p-2.5">
                      <p className="line-clamp-2 text-sm font-semibold text-slate-800">{g.name}</p>
                      <p className="text-[11px] text-slate-400">{g.brand} · {g.unit}</p>
                      <div className="mt-auto pt-2">
                        <p className="text-xs text-slate-400">from</p>
                        <p className="font-mono text-base font-black text-slate-900">{rupee(g.best_price)}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[10px] font-semibold text-brand">
                          <span className="inline-flex items-center gap-0.5"><Store className="h-3 w-3" />{g.offer_count} {g.offer_count === 1 ? "store" : "stores"}</span>
                          {g.nearest_km != null && g.nearest_km <= 50 && <span className="inline-flex items-center gap-0.5 text-slate-500"><MapPin className="h-3 w-3" />{g.nearest_km} km</span>}
                          {g.min_eta && <span className="inline-flex items-center gap-0.5 text-slate-500"><Clock className="h-3 w-3" />{g.min_eta}m</span>}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          ) : (!data.stores?.length && <EmptyState icon={SearchIcon} title={`No results for "${q}"`} subtitle="Try a different keyword — we also understand common terms like 'atta' or 'coke'." />)}
        </>
      )}

      <ProductOffers group={active} onClose={() => setActive(null)} />
    </CustomerLayout>
  );
}
